import { Button } from '@/components/ui/Button';
import { FormErrorMessage } from '@/components/ui/FormErrorMessage';
import { ToggleSwitch } from '@/components/ui/ToggleSwitch';
import { AppVersion, AppVersionFormData } from '@/pages/app-versions/types';
import { mdiAndroid, mdiApple, mdiCheck, mdiClose } from '@mdi/js';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

interface AppVersionFormProps {
  appVersion?: AppVersion | null;
  onSubmit: (data: AppVersionFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const getFormValues = (appVersion?: AppVersion | null): AppVersionFormData => ({
  platform: appVersion?.platform || 'ios',
  minimumVersion: appVersion?.minimumVersion || '',
  latestVersion: appVersion?.latestVersion || '',
  forceUpdate: appVersion?.forceUpdate ?? false,
  updateMessage: appVersion?.updateMessage || '',
  appStoreUrl: appVersion?.appStoreUrl || '',
  enabled: appVersion?.enabled ?? true,
});

export const AppVersionForm: React.FC<AppVersionFormProps> = ({
  appVersion,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const isEditing = !!appVersion;

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
  } = useForm<AppVersionFormData>({
    defaultValues: getFormValues(appVersion),
    mode: 'onChange',
  });

  React.useEffect(() => {
    if (appVersion) {
      reset(getFormValues(appVersion));
    }
  }, [appVersion, reset]);

  const platform = watch('platform');
  const forceUpdate = watch('forceUpdate');
  const isEnabled = watch('enabled');

  const handleFormSubmit = async (data: AppVersionFormData) => {
    await onSubmit(data);
  };

  return (
    <div className='p-6'>
      <div className='flex items-center space-x-3 mb-6 pb-4 border-b border-grey-200'>
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center ${
            platform === 'ios' ? 'bg-grey-100' : 'bg-green-100'
          }`}
        >
          <div
            className={`w-6 h-6 ${platform === 'ios' ? 'text-grey-800' : 'text-green-600'}`}
          >
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d={platform === 'ios' ? mdiApple : mdiAndroid} />
            </svg>
          </div>
        </div>
        <div>
          <h3 className='text-lg font-semibold text-grey-900'>
            {isEditing ? 'Edit App Version' : 'Create New App Version'}
          </h3>
          <p className='text-sm text-grey-600'>
            {isEditing
              ? 'Update version requirements'
              : 'Configure version requirements for a platform'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(handleFormSubmit)} className='space-y-6'>
        {/* Platform Selection - Only in create mode */}
        {!isEditing && (
          <div className='bg-grey-50 rounded-lg p-4'>
            <h4 className='text-sm font-medium text-grey-700 mb-4'>Platform</h4>
            <Controller
              name='platform'
              control={control}
              rules={{ required: 'Platform is required' }}
              render={({ field }) => (
                <div className='flex space-x-4'>
                  <button
                    type='button'
                    onClick={() => field.onChange('ios')}
                    className={`flex-1 flex items-center justify-center space-x-2 p-4 rounded-lg border-2 transition-colors ${
                      field.value === 'ios'
                        ? 'border-grey-800 bg-grey-50'
                        : 'border-grey-200 hover:border-grey-300'
                    }`}
                  >
                    <div className='w-8 h-8 text-grey-800'>
                      <svg viewBox='0 0 24 24' fill='currentColor'>
                        <path d={mdiApple} />
                      </svg>
                    </div>
                    <span className='font-medium'>iOS</span>
                  </button>
                  <button
                    type='button'
                    onClick={() => field.onChange('android')}
                    className={`flex-1 flex items-center justify-center space-x-2 p-4 rounded-lg border-2 transition-colors ${
                      field.value === 'android'
                        ? 'border-green-600 bg-green-50'
                        : 'border-grey-200 hover:border-grey-300'
                    }`}
                  >
                    <div className='w-8 h-8 text-green-600'>
                      <svg viewBox='0 0 24 24' fill='currentColor'>
                        <path d={mdiAndroid} />
                      </svg>
                    </div>
                    <span className='font-medium'>Android</span>
                  </button>
                </div>
              )}
            />
          </div>
        )}

        {/* Version Numbers */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Version Numbers
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Minimum Version *
              </label>
              <Controller
                name='minimumVersion'
                control={control}
                rules={{
                  required: 'Minimum version is required',
                  pattern: {
                    value: /^\d+\.\d+\.\d+$/,
                    message: 'Must be in format X.Y.Z (e.g., 1.0.0)',
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.minimumVersion
                        ? 'border-red-300'
                        : 'border-grey-300'
                    }`}
                    placeholder='1.0.0'
                  />
                )}
              />
              <p className='text-xs text-grey-500 mt-1'>
                Users below this version will be forced to update
              </p>
              <FormErrorMessage message={errors.minimumVersion?.message} />
            </div>
            <div>
              <label className='block text-sm font-medium text-grey-700 mb-1'>
                Latest Version *
              </label>
              <Controller
                name='latestVersion'
                control={control}
                rules={{
                  required: 'Latest version is required',
                  pattern: {
                    value: /^\d+\.\d+\.\d+$/,
                    message: 'Must be in format X.Y.Z (e.g., 1.0.0)',
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    type='text'
                    className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.latestVersion
                        ? 'border-red-300'
                        : 'border-grey-300'
                    }`}
                    placeholder='1.2.0'
                  />
                )}
              />
              <p className='text-xs text-grey-500 mt-1'>
                Current version available in the store
              </p>
              <FormErrorMessage message={errors.latestVersion?.message} />
            </div>
          </div>
        </div>

        {/* Update Settings */}
        <div
          className={`rounded-lg p-4 ${forceUpdate ? 'bg-orange-50' : 'bg-grey-50'}`}
        >
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Update Settings
          </h4>
          <div className='flex items-center justify-between mb-4'>
            <div>
              <p className='text-sm font-medium text-grey-900'>Force Update</p>
              <p className='text-xs text-grey-500 mt-1'>
                {forceUpdate
                  ? 'Users will be forced to update regardless of version'
                  : 'Users can skip update if above minimum version'}
              </p>
            </div>
            <Controller
              name='forceUpdate'
              control={control}
              render={({ field }) => (
                <ToggleSwitch value={field.value} onChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Update Message */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Update Message (Optional)
          </h4>
          <Controller
            name='updateMessage'
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: 'Message must be at most 500 characters',
              },
            }}
            render={({ field }) => (
              <textarea
                {...field}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.updateMessage ? 'border-red-300' : 'border-grey-300'
                }`}
                placeholder='A new version is available with exciting new features...'
              />
            )}
          />
          <p className='text-xs text-grey-500 mt-1'>
            Message shown to users when an update is available
          </p>
          <FormErrorMessage message={errors.updateMessage?.message} />
        </div>

        {/* Store URL */}
        <div className='bg-grey-50 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Store URL (Optional)
          </h4>
          <Controller
            name='appStoreUrl'
            control={control}
            rules={{
              maxLength: {
                value: 500,
                message: 'URL must be at most 500 characters',
              },
              pattern: {
                value: /^https?:\/\/.+/,
                message:
                  'Must be a valid URL starting with http:// or https://',
              },
            }}
            render={({ field }) => (
              <input
                {...field}
                type='url'
                className={`w-full px-3 py-2 border rounded-md bg-white text-grey-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  errors.appStoreUrl ? 'border-red-300' : 'border-grey-300'
                }`}
                placeholder={
                  platform === 'ios'
                    ? 'https://apps.apple.com/app/...'
                    : 'https://play.google.com/store/apps/...'
                }
              />
            )}
          />
          <p className='text-xs text-grey-500 mt-1'>
            Link to the app in the{' '}
            {platform === 'ios' ? 'App Store' : 'Play Store'}
          </p>
          <FormErrorMessage message={errors.appStoreUrl?.message} />
        </div>

        {/* Version Status */}
        <div
          className={`rounded-lg p-4 ${isEnabled ? 'bg-green-50' : 'bg-red-50'}`}
        >
          <h4 className='text-sm font-medium text-grey-700 mb-4'>
            Version Status
          </h4>
          <div className='flex items-center justify-between'>
            <div>
              <p className='text-sm font-medium text-grey-900'>
                {isEnabled ? 'Version Check Enabled' : 'Version Check Disabled'}
              </p>
              <p className='text-xs text-grey-500 mt-1'>
                {isEnabled
                  ? 'Version checking is active for this platform'
                  : 'Version checking is disabled for this platform'}
              </p>
            </div>
            <Controller
              name='enabled'
              control={control}
              render={({ field }) => (
                <ToggleSwitch value={field.value} onChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className='flex items-center justify-end space-x-3 pt-6 border-t border-grey-200'>
          <Button
            type='button'
            label='Cancel'
            icon={mdiClose}
            color='light'
            onClick={onCancel}
            disabled={loading}
          />
          <Button
            type='submit'
            label={isEditing ? 'Update Version' : 'Create Version'}
            icon={mdiCheck}
            color='primary'
            loading={loading}
            disabled={!isValid || (!isDirty && isEditing)}
          />
        </div>
      </form>
    </div>
  );
};
